/**
 * <brick-cookie-consent> — cookie banner + preferences dialog.
 *
 * Wired to Shopify's Customer Privacy API (consent-tracking-api 0.1):
 *  - shows only when the region needs a banner (shouldShowBanner) and the
 *    visitor has not decided yet (currentVisitorConsent);
 *  - writes choices with setTrackingConsent, so Shopify analytics/marketing
 *    pixels follow them. sale_of_data mirrors "Marketing".
 * Fallback: when the API is blocked or not ready within 3 s, the choice is kept
 * in localStorage only. Every decision dispatches `brick:consent` on document
 * (detail: { preferences, analytics, marketing, sale_of_data, source, initial }).
 * Public helper: window.BrickConsent.open() / .get().
 */
(() => {
  if (customElements.get('brick-cookie-consent')) return;

  const STORAGE_KEY = 'brick:consent';
  const API_TIMEOUT = 3000;
  const OPTIONAL = ['preferences', 'analytics', 'marketing'];
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const readStored = () => {
    try {
      const value = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
      return value && typeof value === 'object' && 'analytics' in value ? value : null;
    } catch (e) {
      return null;
    }
  };

  const writeStored = (consent) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...consent, ts: Date.now() }));
    } catch (e) {
      /* storage disabled — the choice still applies for this page view */
    }
  };

  const privacyApi = () => {
    const api = window.Shopify && window.Shopify.customerPrivacy;
    return api && typeof api.setTrackingConsent === 'function' ? api : null;
  };

  /** Resolves with the Customer Privacy API, or null after API_TIMEOUT. */
  const loadPrivacyApi = () =>
    new Promise((resolve) => {
      let settled = false;
      let poll = 0;
      const finish = (api) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        window.clearInterval(poll);
        resolve(api || null);
      };
      const timer = window.setTimeout(() => finish(privacyApi()), API_TIMEOUT);

      if (privacyApi()) return finish(privacyApi());

      const request = () => {
        try {
          window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
            finish(error ? null : privacyApi());
          });
        } catch (e) {
          finish(null);
        }
      };

      if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
        request();
      } else {
        // content_for_header may define Shopify.loadFeatures late (or never, with blockers).
        poll = window.setInterval(() => {
          if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
            window.clearInterval(poll);
            request();
          }
        }, 150);
      }
    });

  const safe = (fn, fallback) => {
    try {
      return fn();
    } catch (e) {
      return fallback;
    }
  };

  class BrickCookieConsent extends HTMLElement {
    connectedCallback() {
      this.banner = this.querySelector('[data-consent-banner]');
      this.dialog = this.querySelector('[data-consent-dialog]');
      this.status = this.querySelector('[data-consent-status]');
      if (!this.banner || !this.dialog) return;

      this.position = this.dataset.position || 'card';
      this.isModalBanner = this.banner.tagName === 'DIALOG';
      this.alwaysShow = this.hasAttribute('data-always-show');
      this.designMode = this.hasAttribute('data-design-mode') || !!(window.Shopify && window.Shopify.designMode);
      this.current = null;
      this.bannerActive = false;
      this.saved = false;

      this.onClick = this.onClick.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.updateOffset = this.updateOffset.bind(this);

      this.addEventListener('click', this.onClick);
      document.addEventListener('click', this.onDocumentClick);
      this.dialog.addEventListener('keydown', this.onKeydown);
      this.dialog.addEventListener('close', () => this.afterDialogClose());
      // Light dismiss on backdrop click (preferences only).
      this.dialog.addEventListener('mousedown', (event) => {
        if (event.target === this.dialog) this.closePreferences();
      });
      if (this.isModalBanner) {
        this.banner.addEventListener('keydown', this.onKeydown);
        // A consent modal needs an explicit choice: ESC does not dismiss it.
        this.banner.addEventListener('cancel', (event) => event.preventDefault());
      }
      window.addEventListener('resize', this.updateOffset, { passive: true });

      window.BrickConsent = {
        open: () => this.openPreferences(),
        get: () => (this.current ? { ...this.current } : null),
      };

      this.revealFooterLinks();
      if (this.designMode) this.bindEditorEvents();

      this.ready = loadPrivacyApi();
      this.ready.then((api) => this.init(api));
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      window.removeEventListener('resize', this.updateOffset);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.launcherObserver) this.launcherObserver.disconnect();
      document.documentElement.style.removeProperty('--cookie-consent-offset');
    }

    /* ------------------------------------------------------------------ */
    /* State                                                               */
    /* ------------------------------------------------------------------ */
    init(api) {
      this.api = api;
      const stored = readStored();
      let decided = false;
      let required = true;

      if (api) {
        const visitor = safe(() => api.currentVisitorConsent(), null) || {};
        decided = OPTIONAL.some((key) => visitor[key] === 'yes' || visitor[key] === 'no');
        required = safe(() => api.shouldShowBanner(), true) !== false;
        if (decided) {
          this.current = this.normalize({
            preferences: visitor.preferences === 'yes',
            analytics: visitor.analytics === 'yes',
            marketing: visitor.marketing === 'yes',
          });
        }
      } else if (stored) {
        decided = true;
        this.current = this.normalize(stored);
      }

      if (this.current) this.emit(this.current, api ? 'shopify' : 'fallback', true);

      const forced = this.alwaysShow;
      if (forced || (required && !decided)) this.showBanner();
    }

    normalize(choice) {
      const consent = {
        preferences: !!choice.preferences,
        analytics: !!choice.analytics,
        marketing: !!choice.marketing,
      };
      consent.sale_of_data = consent.marketing;
      return consent;
    }

    save(choice) {
      const consent = this.normalize(choice);
      this.current = consent;
      this.saved = true;
      writeStored(consent);

      this.hideBanner();
      if (this.dialog.open) this.dialog.close();
      this.announce();

      const apply = (api) => {
        if (!api) {
          this.emit(consent, 'fallback');
          return;
        }
        try {
          api.setTrackingConsent(
            {
              marketing: consent.marketing,
              analytics: consent.analytics,
              preferences: consent.preferences,
              sale_of_data: consent.sale_of_data,
            },
            (result) => this.emit(consent, result && result.error ? 'fallback' : 'shopify')
          );
        } catch (e) {
          this.emit(consent, 'fallback');
        }
      };

      // The API may have loaded after the 3 s fallback window — use it if so.
      const late = this.api || privacyApi();
      if (late) apply(late);
      else this.ready.then((api) => apply(api || privacyApi()));
    }

    emit(consent, source, initial = false) {
      document.dispatchEvent(
        new CustomEvent('brick:consent', { detail: { ...consent, source, initial } })
      );
    }

    announce() {
      if (!this.status) return;
      this.status.textContent = '';
      window.setTimeout(() => {
        this.status.textContent = this.dataset.textSaved || '';
      }, 60);
    }

    /* ------------------------------------------------------------------ */
    /* Banner                                                              */
    /* ------------------------------------------------------------------ */
    showBanner() {
      this.bannerActive = true;
      this.banner.classList.remove('is-leaving');
      if (this.isModalBanner) {
        if (!this.banner.open) this.banner.showModal();
      } else {
        this.banner.hidden = false;
        this.observeBanner();
      }
      this.updateOffset();
    }

    hideBanner(keepActive = false) {
      if (!keepActive) this.bannerActive = false;
      if (this.isModalBanner) {
        if (this.banner.open) this.banner.close();
        return;
      }
      if (this.banner.hidden) return;
      const done = () => {
        this.banner.hidden = true;
        this.banner.classList.remove('is-leaving');
        this.updateOffset();
      };
      if (reducedMotion()) return done();
      this.banner.classList.add('is-leaving');
      this.banner.addEventListener('animationend', done, { once: true });
      window.setTimeout(() => !this.banner.hidden && this.banner.classList.contains('is-leaving') && done(), 400);
    }

    observeBanner() {
      if (this.observing) return;
      this.observing = true;
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.updateOffset);
        this.resizeObserver.observe(this.banner);
      }
      // The chat launcher appears after a delay: re-measure when it does.
      const launcher = document.querySelector('.brick-chat__launcher');
      if (launcher) {
        this.launcherObserver = new MutationObserver(this.updateOffset);
        this.launcherObserver.observe(launcher, { attributes: true, attributeFilter: ['hidden', 'class', 'style'] });
      }
    }

    /** Lift the help-chat launcher above the banner while it overlaps. */
    updateOffset() {
      const root = document.documentElement;
      if (this.isModalBanner || this.banner.hidden || this.banner.classList.contains('is-leaving')) {
        root.style.setProperty('--cookie-consent-offset', '0px');
        return;
      }
      const rect = this.banner.getBoundingClientRect();
      const launcher = document.querySelector('.brick-chat__launcher');
      let overlaps = true;
      if (launcher && !launcher.hidden) {
        const l = launcher.getBoundingClientRect();
        overlaps = l.width === 0 || (l.left < rect.right && l.right > rect.left);
      }
      const offset = overlaps ? Math.max(0, Math.round(window.innerHeight - rect.top)) : 0;
      root.style.setProperty('--cookie-consent-offset', `${offset}px`);
    }

    /* ------------------------------------------------------------------ */
    /* Preferences dialog                                                  */
    /* ------------------------------------------------------------------ */
    openPreferences() {
      const consent = this.current || {};
      this.dialog.querySelectorAll('[data-consent-toggle]').forEach((input) => {
        input.checked = !!consent[input.dataset.consentToggle];
      });
      this.saved = false;
      const active = document.activeElement;
      this.returnFocus = active && active !== document.body ? active : null;
      if (this.isModalBanner && this.banner.open) this.hideBanner(true);
      if (!this.dialog.open) this.dialog.showModal();
      const first = this.dialog.querySelector('[data-consent-toggle]');
      if (first) first.focus();
    }

    closePreferences() {
      if (this.dialog.open) this.dialog.close();
    }

    afterDialogClose() {
      if (!this.saved && this.bannerActive && this.isModalBanner) {
        this.showBanner();
        const first = this.banner.querySelector('[data-consent-action]');
        if (first) first.focus();
        return;
      }
      let target = this.returnFocus;
      if (!target || !target.isConnected || target.closest('[hidden]')) {
        target = this.bannerActive ? this.banner.querySelector('[data-consent-action="customize"]') : null;
      }
      if (target && typeof target.focus === 'function') target.focus({ preventScroll: true });
      this.returnFocus = null;
    }

    readToggles() {
      const choice = {};
      this.dialog.querySelectorAll('[data-consent-toggle]').forEach((input) => {
        choice[input.dataset.consentToggle] = input.checked;
      });
      return choice;
    }

    /* ------------------------------------------------------------------ */
    /* Events                                                              */
    /* ------------------------------------------------------------------ */
    onClick(event) {
      const trigger = event.target.closest('[data-consent-action]');
      if (!trigger || !this.contains(trigger)) return;
      event.preventDefault();
      switch (trigger.dataset.consentAction) {
        case 'accept':
          this.save({ preferences: true, analytics: true, marketing: true });
          break;
        case 'reject':
          this.save({ preferences: false, analytics: false, marketing: false });
          break;
        case 'save':
          this.save(this.readToggles());
          break;
        case 'customize':
          this.openPreferences();
          break;
        case 'close':
          this.closePreferences();
          break;
        default:
      }
    }

    onDocumentClick(event) {
      // Also catch Shopify's own "Cookie preferences" links (e.g. added to a menu).
      const trigger = event.target.closest('[data-cookie-settings], a[href$="#shopifyReshowConsentBanner"]');
      if (!trigger) return;
      event.preventDefault();
      this.openPreferences();
    }

    /** Focus trap (Tab / Shift+Tab wrap) inside an open modal. */
    onKeydown(event) {
      if (event.key !== 'Tab') return;
      const container = event.currentTarget;
      const items = Array.from(container.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement
      );
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && (document.activeElement === first || !container.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    revealFooterLinks() {
      document.querySelectorAll('[data-cookie-settings][hidden]').forEach((el) => {
        el.hidden = false;
      });
    }

    bindEditorEvents() {
      const mine = (event) => event.detail && event.detail.sectionId === this.dataset.sectionId;
      document.addEventListener('shopify:section:select', (event) => {
        if (mine(event)) this.showBanner();
      });
      document.addEventListener('shopify:section:deselect', (event) => {
        if (mine(event) && !this.alwaysShow) {
          if (this.dialog.open) this.dialog.close();
          this.hideBanner();
        }
      });
    }
  }

  customElements.define('brick-cookie-consent', BrickCookieConsent);
})();
